import { Laptop, ShoppingBag, Smartphone, Palette, Layers } from "lucide-react";

export const services = [
  {
    id: "web-development",
    title: "Website Development",
    shortTitle: "Website Development",
    icon: Laptop,
    badge: "Web Engineering",
    summary:
      "Custom websites and WordPress solutions designed around your brand, business goals, customers, and workflows.",
    hubCta: "Explore Website Development",
    contactLabel: "Website Development",
    hero: {
      eyebrow: "Web Development",
      headline: "Websites Built Around Your Business",
      text: "BizLogic develops modern websites and WordPress solutions around a company’s brand, customers, goals, and day-to-day workflows. The result is a site your team can manage and your customers can actually use.",
      primaryLabel: "Start a Project",
      secondaryLabel: "View Our Work",
      secondaryTo: "/#portfolio-section"
    },
    sections: [
      {
        type: "text-points",
        label: "Service introduction",
        title: "More Than a Website",
        paragraphs: [
          "A business website can do more than publish pages. It can collect leads, book appointments, present services, and connect the public site to the tools the company already uses.",
          "This service is for companies that need a clear public presence: service businesses, professional firms, and brands that want the site to support real operations, not only marketing copy."
        ],
        points: [
          "Lead generation",
          "Appointment booking",
          "Customer interactions",
          "Integrations",
          "Content management",
          "E-commerce",
          "Data collection",
          "Business workflows",
          "Custom functionality"
        ]
      },
      {
        type: "items",
        label: "What we build",
        title: "What We Build",
        intro: "The build is scoped to the business. These are the kinds of websites this service covers.",
        items: [
          "Custom WordPress websites",
          "Marketing websites",
          "Business websites",
          "Service business websites",
          "Real estate websites",
          "Healthcare websites",
          "Legal websites",
          "Lead-generation websites",
          "Booking websites",
          "Content-driven websites",
          "Custom WordPress solutions"
        ]
      },
      {
        type: "detail",
        label: "WordPress development",
        title: "WordPress Development",
        paragraphs: [
          "WordPress is a practical choice when your team needs to publish and update content without a developer for every change. We use it when the editorial workflow, plugins, and custom features fit the project.",
          "Custom work stays in the theme, plugins, and content model so the site remains editable after launch."
        ],
        items: [
          "Custom WordPress development",
          "Custom themes",
          "Elementor",
          "Gutenberg",
          "Advanced Custom Fields",
          "Custom post types",
          "Custom taxonomies",
          "Custom PHP",
          "Custom plugins",
          "REST APIs",
          "Third-party integrations",
          "Booking systems",
          "CRM integrations"
        ]
      },
      {
        type: "detail",
        label: "Website performance",
        title: "Built for Performance",
        paragraphs: [
          "A site that is hard to use on a phone, or slow to load, loses the visit before the content matters. Performance and structure are part of the build, not a separate cleanup at the end."
        ],
        items: [
          "Responsive design",
          "Page speed",
          "Image optimization",
          "Clean code",
          "SEO-friendly structure",
          "Security",
          "Caching",
          "Performance monitoring"
        ]
      },
      {
        type: "chips",
        label: "Website integrations",
        title: "Business Integrations",
        intro: "When the website needs to talk to another system, we connect the pieces that the workflow actually requires.",
        items: ["CRM", "Email platforms", "Payment gateways", "Booking platforms", "APIs", "Webhooks", "Analytics", "Marketing tools"]
      },
      {
        type: "process",
        label: "How we work",
        title: "Web Development Process",
        steps: [
          { name: "Discovery", text: "We review the business, audience, content, and the jobs the site needs to do." },
          { name: "Planning", text: "We define pages, content structure, integrations, and what is in or out of the first release." },
          { name: "Design", text: "Layouts are shaped around the content and the actions visitors should take." },
          { name: "Development", text: "The site is built in WordPress or a custom front end, with the editing model your team will use." },
          { name: "Testing", text: "We check content, forms, devices, and the integrations included in the scope." },
          { name: "Launch", text: "The site goes live with redirects, analytics, and a handover your team can follow." },
          { name: "Support", text: "After launch we can handle updates, fixes, and the next set of changes." }
        ]
      },
      {
        type: "faq",
        label: "FAQ",
        title: "Website Development Questions",
        items: [
          {
            q: "What types of websites do you build?",
            a: "Marketing sites, business and service sites, content sites, booking sites, lead-generation sites, and custom WordPress builds. The page list follows the business, not a fixed template."
          },
          {
            q: "Can you work with an existing WordPress website?",
            a: "Yes, when the current theme, plugins, and hosting can support the change. We review what should stay, what should be rebuilt, and what would be safer to replace."
          },
          {
            q: "Can you build custom WordPress functionality?",
            a: "Yes. That can include custom themes, post types, taxonomies, plugins, PHP, and REST API connections when the content model needs more than a standard page."
          },
          {
            q: "Can you integrate third-party systems?",
            a: "Yes, where the other system offers an API, webhook, or supported connection. Typical examples are CRM, email, payments, booking, and analytics."
          },
          {
            q: "Can you maintain the website after launch?",
            a: "Yes. Maintenance can cover updates, fixes, content support, and later features. The scope is agreed separately from the initial build."
          }
        ]
      },
      {
        type: "cta",
        title: "Have a Website Project in Mind?",
        text: "Tell us what the site needs to do for the business. We will outline a practical way to plan and build it.",
        label: "Let's Build Your Website"
      }
    ]
  },
  {
    id: "shopify-development",
    title: "Shopify Store Development",
    shortTitle: "Shopify Stores",
    icon: ShoppingBag,
    badge: "E-Commerce",
    summary:
      "Shopify storefronts designed for clear product discovery, a usable mobile shop, and a straightforward path to checkout.",
    hubCta: "Explore Shopify Development",
    contactLabel: "Shopify Store Development",
    hero: {
      eyebrow: "Shopify Development",
      headline: "Shopify Stores Built to Sell",
      text: "BizLogic creates professional Shopify experiences focused on usability, product discovery, performance, and conversion. The store should make products easy to find and the purchase easy to finish.",
      primaryLabel: "Build Your Shopify Store"
    },
    sections: [
      {
        type: "text-points",
        label: "Service introduction",
        title: "A Storefront Shaped Around the Catalog",
        paragraphs: [
          "This service is for brands selling on Shopify, whether the store is new or the current theme no longer fits the catalog. We focus on how shoppers browse, compare, and buy.",
          "Theme work, product presentation, and the apps the store actually needs are planned together so the shop stays manageable after launch."
        ],
        points: [
          "Product discovery",
          "Collection structure",
          "Mobile shopping",
          "Cart and checkout clarity",
          "Theme customization",
          "Store operations"
        ]
      },
      {
        type: "items",
        label: "Shopify store development",
        title: "What We Build",
        intro: "Storefront work is scoped to the catalog and the way you fulfill orders.",
        items: [
          "Shopify storefronts",
          "Custom Shopify themes",
          "Theme customization",
          "Product pages",
          "Collection pages",
          "Product filtering",
          "Search",
          "Navigation",
          "Cart experiences",
          "Customer accounts",
          "Responsive storefronts"
        ]
      },
      {
        type: "chips",
        label: "Shopify integrations",
        title: "Shopify Integrations",
        intro: "Shopify already covers a large part of commerce. We connect the additional systems the store needs to operate.",
        items: ["Payment systems", "Shipping", "Inventory", "Marketing tools", "Analytics", "Customer management", "Third-party applications"]
      },
      {
        type: "detail",
        label: "E-commerce experience",
        title: "Conversion-Focused Experiences",
        paragraphs: [
          "Conversion work here means removing friction: shoppers can find a product, understand it, and complete the purchase without hunting through the theme.",
          "We look at navigation, product pages, mobile layout, and the cart. Checkout itself stays within Shopify’s supported flow."
        ],
        items: [
          "Product discovery",
          "Clear navigation",
          "Product presentation",
          "Mobile experience",
          "Checkout experience",
          "Calls to action",
          "Trust elements"
        ]
      },
      {
        type: "process",
        label: "How we work",
        title: "Shopify Development Process",
        steps: [
          { name: "Strategy", text: "We review the catalog, customers, and what the current store makes difficult." },
          { name: "UX/UI", text: "Navigation, collections, and product pages are planned before theme code starts." },
          { name: "Development", text: "The theme is customized or built to match that structure." },
          { name: "Integrations", text: "Payments, shipping, inventory, and the apps in scope are connected." },
          { name: "Testing", text: "We test browse, cart, accounts, and the purchase path on desktop and mobile." },
          { name: "Launch", text: "The store goes live with the content and settings required to take orders." },
          { name: "Optimization", text: "After launch we can adjust merchandising, speed, and theme details from real shopper behavior." }
        ]
      },
      {
        type: "faq",
        label: "FAQ",
        title: "Shopify Questions",
        items: [
          {
            q: "Do you build new Shopify stores and update existing ones?",
            a: "Both. A new store starts from the catalog and brand. An existing store starts from what the current theme and apps already do well."
          },
          {
            q: "Can you build a custom Shopify theme?",
            a: "Yes. We can also customize an existing theme when a full rebuild is more than the store needs."
          },
          {
            q: "Which systems can you connect?",
            a: "Payments, shipping, inventory, marketing, analytics, and other apps Shopify can integrate with. The exact list depends on the tools you already use."
          },
          {
            q: "Will you change Shopify checkout?",
            a: "Checkout stays inside Shopify’s supported setup. We improve the path into checkout through navigation, product pages, cart, and theme clarity."
          }
        ]
      },
      {
        type: "cta",
        title: "Ready to Open or Rebuild the Store?",
        text: "Share the catalog and what shoppers struggle with today. We will propose a Shopify scope that matches the store.",
        label: "Build Your Shopify Store"
      }
    ]
  },
  {
    id: "mobile-development",
    title: "Mobile App Development",
    shortTitle: "Mobile Apps",
    icon: Smartphone,
    badge: "Mobile",
    summary:
      "Mobile applications and cross-platform experiences planned around a specific customer or team workflow.",
    hubCta: "Explore Mobile App Development",
    contactLabel: "Mobile App Development",
    hero: {
      eyebrow: "Mobile App Development",
      headline: "Mobile Experiences Built for Your Customers",
      text: "BizLogic builds mobile applications and cross-platform experiences around a defined business or customer workflow. The platform choice follows the product, the users, and the systems it needs to reach.",
      primaryLabel: "Let's Talk About It"
    },
    sections: [
      {
        type: "text-points",
        label: "Who it is for",
        title: "An App for a Specific Job",
        paragraphs: [
          "This service fits companies that need a mobile product for customers, staff, or both. It is not a generic app template. The screens follow the tasks people repeat.",
          "We talk through accounts, data, notifications, and any website or system the app must stay in sync with before development starts."
        ],
        points: [
          "Customer-facing products",
          "Staff and internal tools",
          "Booking and service flows",
          "Account management",
          "Products that need an API"
        ]
      },
      {
        type: "items",
        label: "Mobile applications",
        title: "What We Can Build",
        intro: "The product type is defined in discovery. Examples of the kinds of applications this service covers:",
        items: [
          "Customer apps",
          "Business apps",
          "Booking apps",
          "Service apps",
          "Account management",
          "Customer portals",
          "Internal business applications",
          "API-connected applications"
        ]
      },
      {
        type: "detail",
        label: "App capabilities",
        title: "Core App Capabilities",
        paragraphs: [
          "Capabilities are included when the product needs them. A booking app and an internal reporting app will not carry the same feature list.",
          "We describe the build as a mobile application or cross-platform experience and confirm the technical approach for that project."
        ],
        items: [
          "Authentication",
          "User profiles",
          "User accounts",
          "Notifications",
          "Forms",
          "Booking",
          "Payments where applicable",
          "API integrations",
          "Dashboards",
          "Data synchronization"
        ]
      },
      {
        type: "flow",
        label: "Integrations",
        title: "Connected to Your Business",
        intro: "An app is useful when it can read and write the same information the rest of the business uses. Where those systems allow it, the application can communicate with a website, database, API, CRM, payment provider, or another platform.",
        steps: ["Mobile app", "API", "Website or database", "CRM or payments", "Notifications"]
      },
      {
        type: "process",
        label: "How we work",
        title: "From Idea to App",
        steps: [
          { name: "Discovery", text: "We define who uses the app, what they need to finish, and which systems are involved." },
          { name: "User Flows", text: "The main tasks are mapped before visual design, so the app stays focused." },
          { name: "UX/UI", text: "Screens are designed for small screens and for the actions that matter most." },
          { name: "Development", text: "The application is built against the agreed flows, accounts, and integrations." },
          { name: "Testing", text: "We test the core tasks, accounts, and data sync on the devices in scope." },
          { name: "Deployment", text: "Release covers the environments and stores the project requires." },
          { name: "Support", text: "After release we can fix issues and extend the product in later releases." }
        ]
      },
      {
        type: "faq",
        label: "FAQ",
        title: "Mobile App Questions",
        items: [
          {
            q: "What kinds of apps can you build?",
            a: "Customer apps, internal business apps, booking and service apps, account areas, and applications that need to talk to an API. The exact product is defined before build."
          },
          {
            q: "Do you build native apps?",
            a: "We scope mobile applications and cross-platform experiences to the product. The technical approach is chosen for that project rather than assumed up front."
          },
          {
            q: "Can the app connect to our website or CRM?",
            a: "Yes, when that system provides an API or another supported connection. We confirm the data that needs to move in both directions."
          },
          {
            q: "Can you help release the app?",
            a: "Deployment is part of the plan, including the stores or internal distribution method the project needs."
          }
        ]
      },
      {
        type: "cta",
        title: "Have an App Idea?",
        text: "Describe the people who will use it and the task it should complete. We will tell you whether a mobile application is the right shape for it.",
        label: "Let's Talk About It"
      }
    ]
  },
  {
    id: "ui-ux-design",
    title: "UI/UX Design",
    shortTitle: "UI/UX Design",
    icon: Palette,
    badge: "Design",
    summary:
      "Product design focused on clarity, navigation, usability, and a structure developers can implement.",
    hubCta: "Explore UI/UX Design",
    contactLabel: "UI/UX Design",
    hero: {
      eyebrow: "UI/UX Design",
      headline: "Design That Makes Digital Products Easier to Use",
      text: "UI/UX at BizLogic is about how a product is understood and used. Visual design matters, and so do clarity, navigation, usability, accessibility, conversion, and consistency across screens.",
      primaryLabel: "Let's Design Your Next Product"
    },
    sections: [
      {
        type: "process",
        label: "Design process",
        title: "Our Design Process",
        steps: [
          { name: "Research", text: "We learn who the product is for, what they are trying to do, and where the current experience gets in the way." },
          { name: "Information Architecture", text: "Content and features are grouped so people can find the next step without guessing." },
          { name: "User Flows", text: "Key tasks are mapped from entry to completion, including the points where someone may stop." },
          { name: "Wireframes", text: "Structure is tested in low fidelity before time is spent on visual detail." },
          { name: "Visual Design", text: "Typography, color, and components are applied so the interface stays consistent." },
          { name: "Prototype", text: "Important flows can be clicked through before development starts." },
          { name: "Development Handoff", text: "Screens, components, and behavior notes are organized for the people building the product." }
        ]
      },
      {
        type: "items",
        label: "What we design",
        title: "What We Design",
        intro: "Design work follows the product you are shipping. That can include:",
        items: [
          "Websites",
          "Web applications",
          "Dashboards",
          "Mobile applications",
          "eCommerce experiences",
          "Customer portals",
          "Internal business tools"
        ]
      },
      {
        type: "detail",
        label: "Design systems",
        title: "Design Systems",
        paragraphs: [
          "A design system keeps later screens from drifting. We define the pieces teams reuse so a new page does not invent a new button, form, or navigation pattern.",
          "The system is only as large as the product needs. A marketing site and a multi-role application do not need the same library."
        ],
        items: [
          "Typography",
          "Colors",
          "Components",
          "Buttons",
          "Forms",
          "Cards",
          "Navigation",
          "Responsive layouts",
          "Consistency"
        ]
      },
      {
        type: "text-points",
        label: "Handoff",
        title: "Designed for Development",
        paragraphs: [
          "Design is useful when engineering can build it without reinterpreting every screen. We structure files, components, and notes so spacing, states, and responsive behavior are visible.",
          "This service can stand alone or run alongside a BizLogic build. If another team is implementing the work, the handoff is written for that team."
        ],
        points: [
          "Component structure",
          "Screen states",
          "Responsive behavior",
          "Content rules",
          "Notes for implementation"
        ]
      },
      {
        type: "faq",
        label: "FAQ",
        title: "UI/UX Questions",
        items: [
          {
            q: "Is this only visual design?",
            a: "No. The work covers how people move through the product, how information is organized, and how the interface looks. Visual design is one part of that."
          },
          {
            q: "What products can you design?",
            a: "Websites, web applications, dashboards, mobile apps, eCommerce flows, customer portals, and internal tools."
          },
          {
            q: "Do you create a design system?",
            a: "When the product will keep growing, yes. We define type, color, components, and layout rules so new screens stay consistent."
          },
          {
            q: "Can another development team build from the designs?",
            a: "Yes. Handoff includes the structure and behavior notes needed to implement the screens."
          }
        ]
      },
      {
        type: "cta",
        title: "Need the Product to Be Clearer?",
        text: "Tell us which product or flow is hard to use. We will start with the task users are trying to complete.",
        label: "Let's Design Your Next Product"
      }
    ]
  },
  {
    id: "custom-applications",
    title: "Custom Web Applications",
    shortTitle: "Custom Apps",
    icon: Layers,
    badge: "Software",
    summary:
      "Web applications, portals, and dashboards built around a specific workflow when generic software no longer fits.",
    hubCta: "Explore Custom Applications",
    contactLabel: "Custom Web Application Development",
    hero: {
      eyebrow: "Custom Web Applications",
      headline: "Software Built Around the Way Your Business Works",
      text: "Some teams outgrow spreadsheets, disconnected tools, and generic software. BizLogic develops custom web applications around the workflow and the data those teams already rely on.",
      primaryLabel: "Let's Build the Right Solution"
    },
    sections: [
      {
        type: "text-points",
        label: "Custom business software",
        title: "Software for a Defined Operation",
        paragraphs: [
          "This service is for a company that can describe the work: who logs in, what they need to see, and which step currently happens in a spreadsheet or another tool.",
          "Custom software is not automatically the right answer. If an existing product already fits, we say so. We build when the workflow, the data, or the customer experience does not fit a packaged tool."
        ],
        points: [
          "A process with several roles",
          "Data that should live in one place",
          "Customers or staff who need a portal",
          "Tools that do not talk to each other"
        ]
      },
      {
        type: "items",
        label: "What we build",
        title: "What We Build",
        intro: "The application is named after the job it does. Typical builds include:",
        items: [
          "SaaS platforms",
          "Customer portals",
          "Admin dashboards",
          "Business dashboards",
          "CRM systems",
          "Booking systems",
          "Internal tools",
          "Data management systems",
          "Lead management systems",
          "Workflow systems",
          "Custom business software"
        ]
      },
      {
        type: "compare",
        label: "Approach",
        title: "Built Around Your Workflow",
        intro: "The difference is practical. One option asks the company to change how it works. The other starts from how the company already works.",
        genericLabel: "Generic software",
        generic: "Change your business process to fit the software.",
        customLabel: "Custom software",
        custom: "Build the software around your business process."
      },
      {
        type: "detail",
        label: "Application capabilities",
        title: "Application Capabilities",
        paragraphs: [
          "These capabilities are building blocks. A lead desk, a client portal, and an internal operations tool will use different combinations.",
          "We include a capability when a role in the workflow needs it, and we leave out features that would only add screens."
        ],
        items: [
          "User authentication",
          "User roles",
          "Permissions",
          "Dashboards",
          "Databases",
          "Forms",
          "Search",
          "Filtering",
          "Data management",
          "Notifications",
          "APIs",
          "Webhooks",
          "Third-party integrations",
          "Data synchronization"
        ]
      },
      {
        type: "flow",
        label: "Connected systems",
        title: "Connected Systems",
        intro: "A custom application can sit between the website and the systems the business already uses, so staff are not copying the same record into several places.",
        steps: ["Website", "Application", "Database", "CRM", "Email / Notifications"]
      },
      {
        type: "items",
        label: "When it fits",
        title: "When Custom Software Makes Sense",
        intro: "Custom software is worth discussing when one or more of these are true. It is not a better default than a product that already solves the problem.",
        items: [
          "Existing tools don't fit the workflow",
          "Manual processes take too much time",
          "Data is spread across multiple systems",
          "Employees rely heavily on spreadsheets",
          "Customers need a dedicated portal",
          "The company needs a custom dashboard",
          "Multiple systems need to communicate"
        ]
      },
      {
        type: "process",
        label: "How we work",
        title: "From Concept to Production",
        steps: [
          { name: "Discovery", text: "We learn the operation, the people involved, and the tools already in use." },
          { name: "Requirements", text: "Roles, records, and the first release are written down before architecture is locked." },
          { name: "Architecture", text: "We choose how data, access, and integrations will be structured." },
          { name: "UX/UI", text: "Screens follow the tasks each role performs, not a generic admin template." },
          { name: "Development", text: "The application is built in increments against those requirements." },
          { name: "Testing", text: "We test permissions, core workflows, and the integrations in scope." },
          { name: "Deployment", text: "The application is released to the environment your team will use." },
          { name: "Support", text: "After launch we can correct issues and plan the next workflow to add." }
        ]
      },
      {
        type: "faq",
        label: "FAQ",
        title: "Custom Application Questions",
        items: [
          {
            q: "When should we build custom software instead of buying it?",
            a: "When the packaged options force a workflow your team will not follow, or when data has to move between systems those products do not connect. If a current tool already fits, custom software is usually the slower path."
          },
          {
            q: "What kinds of applications do you build?",
            a: "Portals, dashboards, CRMs, booking tools, lead and data systems, internal tools, and SaaS products. The first release is limited to the workflow you need live."
          },
          {
            q: "Can the application connect to our other systems?",
            a: "Yes, when those systems expose an API, webhook, or another supported method. We map which records need to sync and which system owns each one."
          },
          {
            q: "How do roles and permissions work?",
            a: "People sign in and see the parts of the application their role allows. Permissions are defined from the jobs in the business, such as admin, staff, or customer."
          },
          {
            q: "What happens after the first release?",
            a: "The first release covers the agreed workflow. Later work can add screens, integrations, or roles once that version is in use."
          }
        ]
      },
      {
        type: "cta",
        title: "Have a Complex Workflow?",
        text: "Describe the process and the tools involved. We will help you decide whether a custom web application is the right solution.",
        label: "Let's Build the Right Solution"
      }
    ]
  }
];

export function getServiceById(id) {
  return services.find((service) => service.id === id);
}
